// libs
import React, {useEffect, useState, useRef} from 'react'
import { toast } from 'react-toastify';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faTrash } from '@fortawesome/free-solid-svg-icons';
import PrintErrorMsg from './print-error-msg';
// components 
import Loading from "./loading";
import Record404 from './record404';
// config & functions 
import ajaxCall from '../utils/ajaxcall'
import config from '../config';
import utils from '../utils';
import { useSelectedAccount } from '../context/account-context';
import '../assets/css/gallery-box.css';

function GalleryBox({orgInfo, handlegalleryimagenamefn, setbrowseimage, selectedimg, token, action='view'}) {
    const { selectedAccountDomain, hasSelectedAccount } = useSelectedAccount();
    const searchBoxRef = useRef(null);
    const [SelectedProduct, setSelectedProduct] = useState(selectedimg);
    const [IsLoading, setIsLoading] = useState(false);
    const [LoadMoreImg, setLoadMoreImg] = useState(false);
    const [ImgList, setImgList] = useState({nextToken: '', data: ''});
    const [Is404, setIs404] = useState(false);
    const [IsError, setIsError] = useState(null);
    const [ImgLocation, setImgLocation] = useState('product');
    const [Show, setShow] = useState(false);
    const [SearchQuery, setSearchQuery] = useState(null);
    const [DeletingFile, setDeletingFile] = useState(null);

    const hideModal = () => {
		setShow(false);
        setbrowseimage(false); // remove component from parent div
	}

    const getImgPath = (location = ImgLocation) => {
        if (location === "generic") return "generic";
        if (location === "banner") return "banner";
        if (location === "product") return "products/large";
        return "card";
    }

    const getPublicHref = (fname) => (
        `${config.assets.products}/sas-ecommerce/${selectedAccountDomain}/${getImgPath()}/${fname}`
    );

    const getImages = async () => {
        if(selectedAccountDomain) {
            setIsLoading(true);
            let apiCall = await ajaxCall.getCall(`${config.api.image}/files`, {projectName: 'inhouseEcommSAS', clientDomain: `sas-ecommerce/${selectedAccountDomain}`, imgLocation: ImgLocation, searchQuery: SearchQuery}, token);
            if(apiCall && apiCall.status) {
                setImgList(apiCall.context.message.data);
                setIsLoading(false);
                setIs404(false);
                setIsError(null);
            } else {
                toast.error('Something went wrong. Please try again :(');
                setIsLoading(false);
                setIs404(true);
                setIsError(null);
            }
        } else {
            setIsError("Choose a seller account in the top navigation to begin.");
            setIsLoading(false);
            setIs404(false);
        }
    }

    const copyImagePath = async (e, fname) => {
        e.preventDefault();
        e.stopPropagation();
        const path = getPublicHref(fname);
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(path);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = path;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'absolute';
                textarea.style.left = '-9999px';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            toast.success('Image path copied');
        } catch (err) {
            toast.error('Could not copy path');
        }
    }

    const deleteImage = async (e, fname) => {
        e.preventDefault();
        e.stopPropagation();
        if (!selectedAccountDomain || !fname || DeletingFile) {
            return;
        }
        const confirmed = window.confirm(`Delete "${fname}" from gallery?${ImgLocation === 'product' ? '\nThis will also remove medium and thumbnail versions.' : ''}`);
        if (!confirmed) {
            return;
        }

        setDeletingFile(fname);
        const apiCall = await ajaxCall.deleteCall(`${config.api.image}/files`, {
            projectName: 'inhouseEcommSAS',
            clientDomain: `sas-ecommerce/${selectedAccountDomain}`,
            imgLocation: ImgLocation,
            fileName: fname,
        }, token);

        if (apiCall && apiCall.status) {
            setImgList((prev) => {
                const currentData = Array.isArray(prev?.data) ? prev.data : [];
                return {
                    ...prev,
                    data: currentData.filter((item) => {
                        const itemName = item.name.substring(item.name.lastIndexOf('/') + 1);
                        return itemName !== fname;
                    }),
                };
            });
            if (SelectedProduct) {
                const remaining = SelectedProduct.split(',').filter((name) => name && name !== fname);
                setSelectedProduct(remaining.join(','));
            }
            toast.success('Image deleted');
        } else {
            const apiMessage = apiCall?.context?.message;
            const errorText = typeof apiMessage === 'string'
                ? apiMessage
                : (apiMessage?.message || 'Could not delete image. Please try again.');
            toast.error(errorText);
        }
        setDeletingFile(null);
    }

    const ListImages = ({list}) => {
        if(Array.isArray(list) && list.length > 0) {
            return <div className="gallery-grid">
                {list.map((d,i) => {
                    let fname = d.name.substring(d.name.lastIndexOf('/')+1)
                    let selected = false;
                    if(SelectedProduct && SelectedProduct.split(',').indexOf(fname) !== -1) {
                        selected = true;
                    }
                    const publicHref = getPublicHref(fname);
                    const isDeleting = DeletingFile === fname;
                    const tileClass = [
                        'gallery-tile',
                        selected ? 'is-selected' : '',
                        isDeleting ? 'is-deleting' : '',
                    ].filter(Boolean).join(' ');

                    return <div className={tileClass} key={d.name || `${fname}-${i}`}>
                        <div className="gallery-tile-toolbar">
                            <OverlayTrigger
                                placement="bottom"
                                overlay={<Tooltip id={`tooltip-name-${i}`}>{fname}</Tooltip>}>
                                <span className="gallery-tile-name">{fname}</span>
                            </OverlayTrigger>
                            <div className="gallery-tile-actions">
                                <OverlayTrigger
                                    placement="bottom"
                                    overlay={<Tooltip id={`tooltip-copy-${i}`}>Copy path</Tooltip>}>
                                    <button
                                        type="button"
                                        className="gallery-tile-action"
                                        onClick={(e) => copyImagePath(e, fname)}
                                        aria-label={`Copy path for ${fname}`}
                                    >
                                        <FontAwesomeIcon icon={faCopy} size="sm" />
                                    </button>
                                </OverlayTrigger>
                                <OverlayTrigger
                                    placement="bottom"
                                    overlay={<Tooltip id={`tooltip-delete-${i}`}>Delete image</Tooltip>}>
                                    <button
                                        type="button"
                                        className="gallery-tile-action is-danger"
                                        onClick={(e) => deleteImage(e, fname)}
                                        disabled={isDeleting}
                                        aria-label={`Delete ${fname}`}
                                    >
                                        <FontAwesomeIcon icon={faTrash} size="sm" />
                                    </button>
                                </OverlayTrigger>
                            </div>
                        </div>
                        {(action === "selectable") ? (
                            <div className="gallery-tile-media" onClick={() => chooseSelection(d.name)}>
                                <img src={`${d.url}`} alt={fname} />
                            </div>
                        ) : (
                            <a className="gallery-tile-media" target="_blank" rel="noreferrer" href={publicHref}>
                                <img src={`${d.url}`} alt={fname} />
                            </a>
                        )}
                    </div>
                })}
            </div>
        } else {
            return null;
        }
    }

    const saveSelection = () => {
        const pString = SelectedProduct;
        handlegalleryimagenamefn(pString);
        hideModal();
    }

    const chooseSelection = async (img) => {
        let fname = await utils.fileNameFromPath(img);
        // if available, remove it else add 
        if(SelectedProduct && SelectedProduct.split(',').indexOf(fname) !== -1) {
            let arr = SelectedProduct.split(',');
            let index = arr.indexOf(fname);
            arr.splice(index,1);
            setSelectedProduct(arr.join())
        } else {
            let fname = await utils.fileNameFromPath(img);
            let sp = SelectedProduct;
            let newProduct = fname;
            if(sp) {
                newProduct = `${sp},${fname}`
            }
            setSelectedProduct(newProduct)
        }
    }

    const loadMore = async (nextToken) => {
        setLoadMoreImg(true);
        let apiCall = await ajaxCall.getCall(`${config.api.image}/files`, {projectName: 'inhouseEcommSAS', imgLocation: ImgLocation, clientDomain: `sas-ecommerce/${selectedAccountDomain}`, nextToken: nextToken}, token);
        if(apiCall && apiCall.status) {
            if(apiCall.context.message.data.data.length > 0) {
                let records = ImgList;
                records.nextToken = (apiCall.context.message.data.hasOwnProperty('nextToken') && apiCall.context.message.data.nextToken !== '')?apiCall.context.message.data.nextToken:"";
                records.data = [...records.data, ...apiCall.context.message.data.data];
                setImgList(records);
                setLoadMoreImg(false);
            } else {
                let records = ImgList;
                records.nextToken = "";
                setImgList(records);
                setLoadMoreImg(false);
            }
        }
    }

    const LoadMoreBtn = ({token}) => {
        return <div className="text-center py-3"><Button type="button" disabled={(LoadMoreImg)?true:false} variant="primary" className="fs-14rem" onClick={() => loadMore(token)}>{(LoadMoreImg)?'Loading...':'Load More'}</Button></div>
    }   

    const handleSearchClick = () => {
        const searchValue = searchBoxRef.current?.value || '';
        setSearchQuery(searchValue);
    };

    const RenderImages = () => {
        if(IsLoading) {
            return <Loading /> 
        } else if (Is404) {
            return <Record404 />
        } else if(ImgList) {
            const d = ImgList.hasOwnProperty('data')?ImgList.data : [];
            return <>
                <ListImages list={d} />
                <div className="container">
                    <div className="row text-center">
                        {(ImgList.hasOwnProperty('nextToken') && ImgList.nextToken !== '') ? <LoadMoreBtn token={ImgList.nextToken} /> : null}
                    </div>
                </div>
            </>
        } else {
            return "Mismatch state value";
        }
    }

    useEffect(() => {
        async function loadComponent() {
            setSelectedProduct(selectedimg);
            setShow(true);
            getImages();
        }
        loadComponent();
    },[ImgLocation, selectedAccountDomain, SearchQuery]);

    return (
        <Modal show={Show} size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
            <Modal.Header>
                <Modal.Title>Browse Gallery</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Container>
                    <Row className="mb-3 mt-3">
                        <Col xl="12" lg="12" className="mb-2">
                            <p className="mb-0 fs-13rem text-muted">
                                {hasSelectedAccount
                                    ? <>Seller: <strong>{selectedAccountDomain}</strong> (change via top navigation)</>
                                    : <>Choose a seller account in the top navigation.</>}
                            </p>
                        </Col>
                        <Col xl="6" lg="6" className="mb-3">
                            <label className="form-label fs-14rem">Location </label>
                            <select value={ImgLocation} className="form-control custom-select border mb-2" name="imgLocation" onChange={(e) => {setImgList({nextToken: '', data: ''}); setImgLocation(e.target.value)}}>
                                <option value=""> Choose...</option>
                                <option value="generic">Generic (Freestyle)</option>
                                <option value="banner">Banner (1800x750)</option>
                                <option value="product">Product (1200x1200)</option>
                                <option value="card">Card (416x416)</option>
                            </select>
                        </Col>
                        <Col xl="12" lg="12">
                            <label className="form-label fs-14rem">Search with Prefix</label>
                            <div className="input-group mb-3">
                                <input type="text" ref={searchBoxRef} defaultValue={SearchQuery} className="form-control" placeholder="Prefix Phrase" aria-label="Prefix Phrase" aria-describedby="button-search" />
                                <Button variant="secondary" className="fs-14rem" type="button" id="button-search" onClick={handleSearchClick}>Search</Button>
                            </div>
                        </Col>
                    </Row>
                </Container>
                {(IsError) ? <Container>
                    <Row className="my-3">
                        <Col lg="12">
                            <div className="text-danger fs-12rem text-center"><PrintErrorMsg msg={IsError} /></div>
                        </Col>
                    </Row>
                </Container> : <RenderImages />}
            </Modal.Body>
            <Modal.Footer>
                <Button variant='secondary' className="fs-14rem" onClick={hideModal}>Close</Button>
                {(action === 'view') ? null:<Button variant="primary" className="fs-14rem" disabled={(SelectedProduct)?false:true} onClick={() => saveSelection()}>Save</Button>}
            </Modal.Footer>
        </Modal>
    )
}
export default GalleryBox;
